import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { createExtensao } from '../../services/extensaoService';
import { useAuth } from '../../contexts/AuthContext';

const initialState = {
  nome: '',
  cidade: '',
  uf: '',
  telefone1: '',
  status: '',
  email: '',
  cnpj: '',
  estrangeira: false,
  pais: '',
  mensalidade: '',
  repasseIBE: '',
  dataAbertura: '',
  nomeIgreja: '',
  nomePastorPresidente: '',
  nomePastorDirigente: '',
  cep: '',
  logradouro: '',
  numero: '',
  bairro: '',
  telefone2: '',
};

const validate = (form) => {
  if (!form.nome.trim()) return 'O nome da extensão é obrigatório.';
  if (form.nome.length > 50) return 'O nome deve ter até 50 caracteres.';
  return '';
};

// Funções utilitárias para máscara
function maskTelefone(value) {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
    .slice(0, 15);
}
function maskCNPJ(value) {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .slice(0, 18);
}
function maskCEP(value) {
  return value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
}
function maskMoney(value) {
  let v = value.replace(/\D/g, '');
  v = (v/100).toFixed(2) + '';
  v = v.replace('.', ',');
  v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  return v;
}

const UF_LIST = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const ExtensaoCreate = () => {
  const { hasPermission } = useAuth();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    // Converter valores monetários para formato numérico padrão
    const formToSend = {
      ...form,
      mensalidade: form.mensalidade ? String(form.mensalidade).replace(/\./g, '').replace(',', '.') : '',
      repasseIBE: form.repasseIBE ? String(form.repasseIBE).replace(/\./g, '').replace(',', '.') : ''
    };
    setShowConfirm(true);
  };

  if (!hasPermission('extensoes.create')) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">Acesso negado: você não tem permissão para cadastrar extensões.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Cadastrar Extensão</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Principais</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input type="text" name="nome" value={form.nome} onChange={handleChange} maxLength={50} required className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400" placeholder="Nome da extensão" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select name="status" value={form.status} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione</option>
                <option value="Ativa">Ativa</option>
                <option value="Inativa">Inativa</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estrangeira</label>
              <div className="flex items-center mt-2">
                <input type="checkbox" name="estrangeira" checked={form.estrangeira || false} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Estrangeira</label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{form.estrangeira ? 'Identificação Social (Equivalente ao CNPJ)' : 'CNPJ'}</label>
              {form.estrangeira ? (
                <input type="text" name="idt_empresa_internacional" value={form.idt_empresa_internacional || ''} onChange={handleChange} maxLength={20} className="w-full border rounded px-3 py-2" placeholder="Identificação Social (Equivalente ao CNPJ)" />
              ) : (
                <input type="text" name="cnpj" value={form.cnpj || ''} onChange={e => handleChange({target: {name: 'cnpj', value: maskCNPJ(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="CNPJ" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input type="text" name="pais" value={form.pais} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="País" />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="font-bold text-lg mb-2 text-green-700">Contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Telefone 1</label>
              <input type="text" name="telefone1" value={form.telefone1} onChange={e => handleChange({target: {name: 'telefone1', value: maskTelefone(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="Telefone 1" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone 2</label>
              <input type="text" name="telefone2" value={form.telefone2} onChange={e => handleChange({target: {name: 'telefone2', value: maskTelefone(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="Telefone 2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Celular</label>
              <input type="text" name="celular" value={form.celular} onChange={e => handleChange({target: {name: 'celular', value: maskTelefone(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="Celular" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Email" />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="font-bold text-lg mb-2 text-green-700">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Cidade</label>
              <input type="text" name="cidade" value={form.cidade} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="Cidade" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">UF</label>
              <select name="uf" value={form.uf || ''} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione</option>
                {UF_LIST.map(uf => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CEP</label>
              <input type="text" name="cep" value={form.cep} onChange={e => handleChange({target: {name: 'cep', value: maskCEP(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="CEP" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Logradouro</label>
              <input type="text" name="logradouro" value={form.logradouro} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Logradouro" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número</label>
              <input type="number" name="numero" value={form.numero} onChange={handleChange} maxLength={10} className="w-full border rounded px-3 py-2" placeholder="Número" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bairro</label>
              <input type="text" name="bairro" value={form.bairro} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="Bairro" />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Financeiros</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mensalidade</label>
              <input type="text" name="mensalidade" value={form.mensalidade} onChange={e => handleChange({target: {name: 'mensalidade', value: maskMoney(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="Mensalidade" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Repasse IBE</label>
              <input type="text" name="repasseIBE" value={form.repasseIBE} onChange={e => handleChange({target: {name: 'repasseIBE', value: maskMoney(e.target.value)}})} className="w-full border rounded px-3 py-2" placeholder="Repasse IBE" />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Institucionais</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Data de Abertura</label>
              <input type="date" name="dataAbertura" value={form.dataAbertura} onChange={handleChange} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nome Igreja</label>
              <input type="text" name="nomeIgreja" value={form.nomeIgreja} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Nome Igreja" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pastor Presidente</label>
              <input type="text" name="nomePastorPresidente" value={form.nomePastorPresidente} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Pastor Presidente" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pastor Dirigente</label>
              <input type="text" name="nomePastorDirigente" value={form.nomePastorDirigente} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Pastor Dirigente" />
            </div>
          </div>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Extensão cadastrada com sucesso!</div>}
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/extensoes')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Cadastro"
        message="Deseja realmente cadastrar esta extensão?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          setLoading(true);
          setError('');
          setSuccess(false);
          try {
            await createExtensao({
              ...form,
              mensalidade: form.mensalidade ? String(form.mensalidade).replace(/\./g, '').replace(',', '.') : '',
              repasseIBE: form.repasseIBE ? String(form.repasseIBE).replace(/\./g, '').replace(',', '.') : ''
            });
            setSuccess(true);
            setTimeout(() => navigate('/extensoes'), 1200);
          } catch (err) {
            setError(err.message || 'Erro ao cadastrar extensão.');
          } finally {
            setLoading(false);
          }
        }}
      />
    </div>
  );
};

export default ExtensaoCreate;
